import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import truncate from 'lodash/truncate';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_TEXT, SCHEMA_TYPE_YOUTUBE } from '../../util/types';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { richText } from '../../util/richText';
import { pickCustomFieldProps } from '../../util/fieldHelpers';

import {
    Page,
    AvatarLarge,
    ListingCard,
    InlineTextButton,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import SectionTextMaybe from '../ProfilePage/SectionTextMaybe';
import SectionMultiEnumMaybe from '../ProfilePage/SectionMultiEnumMaybe';
import SectionYoutubeVideoMaybe from '../ProfilePage/SectionYoutubeVideoMaybe';

import css from './ShopStorefrontPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 20;
const BIO_COLLAPSED_LENGTH = 200;

// Truncate helper for bio
const truncateBio = s => {
    return truncate(s, {
        length: BIO_COLLAPSED_LENGTH,
        separator: /\s|,|\.|:|;/,
        omission: '…',
    });
};

// Collapsible bio component
const CollapsibleBio = ({ bio }) => {
    const [expanded, setExpanded] = useState(false);

    if (!bio) return null;

    const isLongBio = bio.length > BIO_COLLAPSED_LENGTH;

    const bioWithLinks = richText(bio, {
        linkify: true,
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
        longWordClass: css.longWord,
    });

    const truncatedBioWithLinks = richText(truncateBio(bio), {
        linkify: true,
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
        longWordClass: css.longWord,
    });

    return (
        <div className={css.bioContainer}>
            <p className={css.shopBio}>
                {expanded ? bioWithLinks : truncatedBioWithLinks}
            </p>
            {isLongBio && !expanded && (
                <InlineTextButton
                    className={css.readMoreButton}
                    onClick={() => setExpanded(true)}
                >
                    <span>Read more</span>
                </InlineTextButton>
            )}
            {isLongBio && expanded && (
                <InlineTextButton
                    className={css.readMoreButton}
                    onClick={() => setExpanded(false)}
                >
                    <span>Read less</span>
                </InlineTextButton>
            )}
        </div>
    );
};

// Custom user fields component for sidebar (website, video, etc.)
const CustomUserFields = ({ publicData, metadata, userFieldConfig, intl }) => {
    const shouldPickUserField = fieldConfig => fieldConfig?.showConfig?.displayInProfile !== false;
    const propsForCustomFields =
        pickCustomFieldProps(publicData, metadata, userFieldConfig, 'userType', shouldPickUserField) ||
        [];

    return (
        <>
            {propsForCustomFields.map(customFieldProps => {
                const { schemaType, key, ...fieldProps } = customFieldProps;
                return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
                    <SectionMultiEnumMaybe key={key} {...fieldProps} />
                ) : schemaType === SCHEMA_TYPE_TEXT ? (
                    <SectionTextMaybe key={key} {...fieldProps} />
                ) : schemaType === SCHEMA_TYPE_YOUTUBE ? (
                    <SectionYoutubeVideoMaybe key={key} {...fieldProps} />
                ) : null;
            })}
        </>
    );
};

// Sidebar component showing shop info
const ShopSidebar = ({ shopUser, userFieldConfig, intl }) => {
    if (!shopUser) return null;

    const profile = shopUser?.attributes?.profile || {};
    const displayName = profile.displayName || '';
    const bio = profile.bio || '';
    const publicData = profile.publicData || {};
    const metadata = profile.metadata || {};

    return (
        <aside className={css.sidebar}>
            <div className={css.sidebarContent}>
                <AvatarLarge className={css.shopAvatar} user={shopUser} disableProfileLink />
                <h1 className={css.shopName}>{displayName}</h1>

                <CollapsibleBio bio={bio} />

                {/* Custom user fields (website, video, etc.) */}
                <CustomUserFields
                    publicData={publicData}
                    metadata={metadata}
                    userFieldConfig={userFieldConfig}
                    intl={intl}
                />
            </div>
        </aside>
    );
};

// Tab selector for All/Tours/Courses
const CategoryTabs = ({ currentCategory, onCategoryChange }) => {
    return (
        <div className={css.tabsContainer}>
            <nav className={css.tabsNav}>
                <button
                    className={classNames(css.tab, { [css.activeTab]: currentCategory === 'all' })}
                    onClick={() => onCategoryChange('all')}
                >
                    <span>All</span>
                </button>
                <button
                    className={classNames(css.tab, { [css.activeTab]: currentCategory === 'tour' })}
                    onClick={() => onCategoryChange('tour')}
                >
                    <span>Tours</span>
                </button>
                <button
                    className={classNames(css.tab, { [css.activeTab]: currentCategory === 'course' })}
                    onClick={() => onCategoryChange('course')}
                >
                    <span>Courses</span>
                </button>
            </nav>
        </div>
    );
};

// Listings grid
const ListingsGrid = ({ listings, isLoading, error }) => {
    if (isLoading) {
        return <div className={css.loading}>Loading...</div>;
    }

    if (error) {
        return <div className={css.error}>Error loading listings</div>;
    }

    if (!listings || listings.length === 0) {
        return (
            <div className={css.noListings}>
                <h3 className={css.noListingsTitle}>No listings found</h3>
                <p className={css.noListingsText}>This shop hasn't added any listings in this category yet.</p>
            </div>
        );
    }

    return (
        <div className={css.listingsGrid}>
            {listings.map(listing => (
                <ListingCard
                    key={listing.id.uuid}
                    listing={listing}
                    showAuthorInfo={false}
                />
            ))}
        </div>
    );
};

/**
 * ShopStorefrontPageComponent
 */
export const ShopStorefrontPageComponent = props => {
    const config = useConfiguration();
    const intl = useIntl();

    // Use React state for category filter - no URL params to avoid page reload
    const [currentCategory, setCurrentCategory] = useState('all');

    const { userFields } = config.user;

    const {
        scrollingDisabled,
        shopUser,
        shopShowError,
        listings,
        queryListingsInProgress,
        queryListingsError,
    } = props;

    // Filter listings client-side based on selected category
    const filteredListings = currentCategory === 'all'
        ? listings
        : listings.filter(listing => {
            const category = listing.attributes?.publicData?.categoryLevel1;
            return category === currentCategory;
        });

    // Handle 404
    if (shopShowError?.status === 404) {
        return <NotFoundPage />;
    }

    // Loading state
    if (!shopUser && !shopShowError) {
        return (
            <Page scrollingDisabled={scrollingDisabled} title="Loading...">
                <TopbarContainer className={css.topbar} />
                <div className={css.layoutWrapper}>
                    <div className={css.loading}>Loading shop...</div>
                </div>
                <FooterContainer />
            </Page>
        );
    }

    // Error state
    if (shopShowError) {
        return (
            <Page scrollingDisabled={scrollingDisabled} title="Error">
                <TopbarContainer className={css.topbar} />
                <div className={css.layoutWrapper}>
                    <div className={css.error}>Error loading shop</div>
                </div>
                <FooterContainer />
            </Page>
        );
    }

    const displayName = shopUser?.attributes?.profile?.displayName || 'Shop';
    const schemaTitle = `${displayName} | ${config.marketplaceName}`;

    return (
        <Page
            className={css.pageRoot}
            scrollingDisabled={scrollingDisabled}
            title={schemaTitle}
            schema={{
                '@context': 'http://schema.org',
                '@type': 'Store',
                name: displayName,
            }}
        >
            <TopbarContainer className={css.topbar} />

            <div className={css.layoutWrapper}>
                <ShopSidebar shopUser={shopUser} userFieldConfig={userFields} intl={intl} />

                <main className={css.mainContent}>
                    <CategoryTabs
                        currentCategory={currentCategory}
                        onCategoryChange={setCurrentCategory}
                    />

                    <div className={css.listingsHeader}>
                        <p className={css.listingsCount}>
                            {filteredListings.length} {filteredListings.length === 1 ? 'listing' : 'listings'}
                        </p>
                    </div>

                    <ListingsGrid
                        listings={filteredListings}
                        isLoading={queryListingsInProgress}
                        error={queryListingsError}
                    />
                </main>
            </div>

            <FooterContainer />
        </Page>
    );
};

const mapStateToProps = state => {
    // Safely access ShopStorefrontPage state with defaults
    const pageState = state.ShopStorefrontPage || {};
    const {
        shopUser = null,
        shopShowError = null,
        listingRefs = [],
        queryListingsInProgress = false,
        queryListingsError = null,
    } = pageState;

    return {
        scrollingDisabled: isScrollingDisabled(state),
        shopUser,
        shopShowError,
        listings: getMarketplaceEntities(state, listingRefs),
        queryListingsInProgress,
        queryListingsError,
    };
};

const ShopStorefrontPage = compose(connect(mapStateToProps))(ShopStorefrontPageComponent);

export default ShopStorefrontPage;
